import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Button, SpaceBetween, Icon } from '@amzn/awsui-components-react';
import { useSelector } from 'react-redux';
import { Auth } from 'aws-amplify';
import { useScript } from '../../hooks/useScript';
import './PulseWidget.scss';

// Declare the global PulseJSWidget constructor
declare global {
  interface Window {
    PulseJSWidget?: any;
  }
}

interface PulseWidgetProps {
  friendlyId?: string;
  formType?: 'Application' | 'Survey';
  displayMode?: 'Inline' | 'Modal' | 'Popup';
  onClose?: () => void;
  requiresAuth?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  theme?: 'light' | 'dark';
  appId?: string;
  debug?: boolean;
  PulseJSWidgetClass: any;
}

// Configuration for different environments
const PULSE_CONFIG = {
  prod: {
    apiUrl: 'https://api.pulse.aws',
    scriptUrl: 'https://widget.pulse.aws.dev/pulse-js-widget.v0.1.0.js',
  },
  gamma: {
    apiUrl: 'https://gamma-api.pulse.aws',
    scriptUrl: 'https://gamma-widget.pulse.aws.dev/pulse-js-widget.v0.1.0.js',
  },
  beta: {
    apiUrl: 'https://gamma-api.pulse.aws',
    scriptUrl: 'https://gamma-widget.pulse.aws.dev/pulse-js-widget.v0.1.0.js',
  },
  alpha: {
    apiUrl: 'https://gamma-api.pulse.aws',
    scriptUrl: 'https://gamma-widget.pulse.aws.dev/pulse-js-widget.v0.1.0.js',
  },
  dev: {
    apiUrl: 'https://gamma-api.pulse.aws',
    scriptUrl: 'https://gamma-widget.pulse.aws.dev/pulse-js-widget.v0.1.0.js',
  },
};

// Helper function to get Cognito access token
const getAccessToken = async (): Promise<string | null> => {
  try {
    const session = await Auth.currentSession();
    const accessToken = session.getAccessToken().getJwtToken();
    return accessToken;
  } catch (error) {
    console.error('Error getting access token for Pulse widget:', error);
    return null;
  }
};

/**
 * PulseWidget - The actual widget that receives the PulseJSWidget class
 * and handles creating the instance and rendering.
 */
export const PulseWidget: React.FC<PulseWidgetProps> = ({
  PulseJSWidgetClass,
  friendlyId,
  formType = 'Application',
  displayMode = 'Modal',
  onClose,
  requiresAuth = true,
  open = false,
  onOpenChange,
  theme = 'light',
  appId = 'AccountingBICentral',
  debug = false,
}) => {
  const currentStage = useSelector((globalState: any) => globalState.auth.currentStage);
  const widgetContainerRef = useRef<HTMLDivElement>(null);
  const pulseWidgetRef = useRef<any>(null);
  const [error, setError] = useState<string | null>(null);

  const config = PULSE_CONFIG[currentStage as keyof typeof PULSE_CONFIG] || PULSE_CONFIG.beta;

  const handleOpenChange = useCallback(
    (status: boolean) => {
      if (onOpenChange) {
        onOpenChange(status);
      }
      if (!status && onClose) {
        onClose();
      }
    },
    [onOpenChange, onClose]
  );

  // Create widget instance on mount
  useEffect(() => {
    if (!pulseWidgetRef.current && widgetContainerRef.current && PulseJSWidgetClass) {
      try {
        pulseWidgetRef.current = new PulseJSWidgetClass(widgetContainerRef.current);
      } catch (err) {
        console.error('Error creating PulseJSWidget instance:', err);
        setError('Failed to initialize feedback widget.');
      }
    }
  }, [PulseJSWidgetClass]);

  // Render widget when props change
  useEffect(() => {
    if (!pulseWidgetRef.current) return;

    const timeoutId = setTimeout(() => {
      try {
        pulseWidgetRef.current.renderPulseJSWidget({
          appId: appId,
          apiUrl: config.apiUrl,
          formType: formType,
          friendlyId: friendlyId,
          displayMode: displayMode,
          theme: theme,
          debug: debug,
          open: open,
          onOpenChange: handleOpenChange,
          authTokenReader: requiresAuth ? getAccessToken : undefined,
        });
      } catch (err) {
        console.error('Error rendering Pulse widget:', err);
        setError('Failed to load feedback form. Please try again later.');
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [open, theme, handleOpenChange, friendlyId, appId, config.apiUrl, formType, displayMode, debug, requiresAuth]);

  if (error) {
    return (
      <Box textAlign="center" padding="l">
        <SpaceBetween direction="vertical" size="s">
          <Icon name="status-warning" variant="warning" size="big" />
          <Box>{error}</Box>
          {onClose && <Button onClick={onClose}>Close</Button>}
        </SpaceBetween>
      </Box>
    );
  }

  return <div id="pulse-widget-container" ref={widgetContainerRef} className="pulse-widget-mount" />;
};

/**
 * PulseWidgetWrapper - Loads the Pulse script and only renders
 * the PulseWidget once window.PulseJSWidget is available.
 */
export const PulseWidgetWrapper: React.FC<Omit<PulseWidgetProps, 'PulseJSWidgetClass'>> = (props) => {
  const currentStage = useSelector((globalState: any) => globalState.auth.currentStage);
  const [scriptReady, setScriptReady] = useState(!!window.PulseJSWidget);

  const config = PULSE_CONFIG[currentStage as keyof typeof PULSE_CONFIG] || PULSE_CONFIG.beta;

  const handleScriptLoad = useCallback(() => {
    if (window.PulseJSWidget) {
      setScriptReady(true);
    }
  }, []);

  useScript(config.scriptUrl, handleScriptLoad);

  // Fallback: poll for window.PulseJSWidget in case the load callback fires
  // before the script has fully initialized the global
  useEffect(() => {
    if (scriptReady) return;

    const checkInterval = setInterval(() => {
      if (window.PulseJSWidget) {
        setScriptReady(true);
        clearInterval(checkInterval);
      }
    }, 200);

    // Stop polling after 10 seconds
    const timeout = setTimeout(() => {
      clearInterval(checkInterval);
    }, 10000);

    return () => {
      clearInterval(checkInterval);
      clearTimeout(timeout);
    };
  }, [scriptReady]);

  if (!scriptReady || !window.PulseJSWidget) {
    return <div className="pulse-widget-mount" />;
  }

  return <PulseWidget {...props} PulseJSWidgetClass={window.PulseJSWidget} />;
};

export default PulseWidgetWrapper;
